import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import BookHeatmap from "@/components/BookHeatmap";
import BookStages from "@/components/BookStages";
import { ProgressBar, StatusBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { Save, Pencil, Info, Calendar, Hash, Tag, StickyNote, AlertTriangle, MapPin, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const fmt = (v) => `${Math.round(Number(v || 0) * 100)}%`;
const HM_COLORS = ["var(--hm-0)", "var(--hm-1)", "var(--hm-2)", "var(--hm-3)"];
const HM_LABELS = ["0%", "1–49%", "50–99%", "100%"];

// Compare heatmap/graph progress (project.progress) with KAIROS stage-derived pct.
// Returns list of book names that disagree by more than DISCREPANCY_TOLERANCE pp
// or whose completion state disagrees.
const DISCREPANCY_TOLERANCE = 5; // percentage points

function computeDiscrepancies(books, progress, stageBooks) {
  if (!Array.isArray(stageBooks) || !stageBooks.length) return [];
  const byName = new Map();
  stageBooks.forEach((sb) => {
    if (sb?.book) byName.set(String(sb.book).trim().toLowerCase(), sb);
  });
  const out = [];
  for (const b of books || []) {
    const sb = byName.get(String(b).trim().toLowerCase());
    if (!sb) continue; // no stage data for this book — skip (not a discrepancy)
    const heatPct = Math.round(Number(progress?.[b] || 0) * 100);
    const stagePct = Math.round(Number(sb.pct || 0) * 100);
    const heatComplete = heatPct >= 100;
    const stageComplete = (sb.current_stage || "").toLowerCase().includes("all stages complete");
    if (Math.abs(heatPct - stagePct) > DISCREPANCY_TOLERANCE || heatComplete !== stageComplete) {
      out.push({ book: b, heatPct, stagePct, heatComplete, stageComplete });
    }
  }
  return out;
}

function DiscrepancyIcon({ items }) {
  if (!items?.length) return null;
  const preview = items.slice(0, 6).map((d) => `${d.book}: heatmap ${d.heatPct}% vs stages ${d.stagePct}%`).join("\n");
  const tip = `Data Discrepancy Detected\n${items.length} book${items.length === 1 ? "" : "s"} disagree between the Book Coverage Heatmap / Book-by-Book graph and the KAIROS Stage Timeline.\n\n${preview}${items.length > 6 ? `\n…and ${items.length - 6} more` : ""}`;
  return (
    <span
      className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 border cursor-help"
      style={{ borderColor: "var(--amber)", color: "var(--amber)" }}
      title={tip}
      data-testid="discrepancy-warning"
    >
      <AlertTriangle size={12} strokeWidth={2} />
      <span className="mono text-[10px] uppercase tracking-widest">
        Data Discrepancy · {items.length}
      </span>
    </span>
  );
}

export default function LanguageProjectDetail() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoDraft, setInfoDraft] = useState({});
  const [infoSaving, setInfoSaving] = useState(false);

  const canEdit = user && (user.role === "admin" || user.can_edit === true) && ["admin", "project_manager", "coordinator"].includes(user.role);

  useEffect(() => { load(); }, [projectId]);

  const [langDetail, setLangDetail] = useState(null);
  const [stageBooks, setStageBooks] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/language-projects/${projectId}/book-stages`)
      .then((r) => setStageBooks(r.data?.books || []))
      .catch(() => setStageBooks([]));
  }, [projectId]);
  useEffect(() => {
    if (data?.project?.iso_code) {
      api.get(`/projects/language-detail/${encodeURIComponent(data.project.iso_code)}`)
        .then((r) => setLangDetail(r.data))
        .catch(() => setLangDetail(null));
    }
  }, [data?.project?.iso_code]);

  const langDetailBlock = langDetail && (
    <section className="mt-8 mb-2" data-testid="lang-db-panel">
      <div className="mb-4">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
          From Language Database · ISO {langDetail.iso}
        </div>
        <h2 className="serif text-3xl tracking-tight">Engagements &amp; Field Log</h2>
      </div>
      {langDetail.engagements?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {langDetail.engagements.map((e) => (
            <div key={e.key} className="hairline bg-white p-5" data-testid={`lang-engagement-${e.key}`}>
              <div className="flex items-baseline gap-3 flex-wrap mb-3">
                <span className="serif text-2xl tracking-tight">{e.country}</span>
                <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)]">
                  {e.iso}{e.rolv ? ` · ${e.rolv}` : ""}
                </span>
                {e.hub && <span className="mono text-[10px] px-1.5 py-0.5 border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>{e.hub}</span>}
                {e.cluster && <span className="mono text-[10px] px-1.5 py-0.5 border border-[color:var(--border)] text-[color:var(--ink-2)]">{e.cluster}</span>}
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Organizations</div><div className="mt-0.5">{(e.organizations || []).join(", ") || "—"}</div></div>
                <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Involvement</div><div className="mt-0.5">{(e.engagement_functions || []).join(" · ") || "—"}</div></div>
                <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Activities</div><div className="mt-0.5">{(e.activities || []).join(", ") || "—"}</div></div>
                <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Status</div><div className="mt-0.5">{(e.statuses || []).join(", ") || "—"}</div></div>
              </div>
              {(e.current_scope?.length || e.completed_products?.length) ? (
                <div className="border-t border-[color:var(--border)] mt-4 pt-3 text-xs space-y-2">
                  {e.current_scope?.length > 0 && (
                    <div>
                      <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)] mb-1">Current Scope</div>
                      <div className="flex flex-wrap gap-1">
                        {e.current_scope.map((s) => (
                          <span key={s} className="mono px-1 py-0.5 border rounded-sm" style={{ fontSize: "9px", borderColor: "var(--accent)", color: "var(--accent)" }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {e.completed_products?.length > 0 && (
                    <div>
                      <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)] mb-1">Completed Products</div>
                      <div className="flex flex-wrap gap-1">
                        {e.completed_products.map((p, i) => (
                          <span key={i} className="chip inline-flex items-center gap-1" style={{ borderColor: "var(--on-track)", color: "var(--on-track)" }}>
                            <PackageCheck size={10} />
                            {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.product}</a> : p.product}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FeedCol title="Notes" icon={StickyNote} accent="var(--on-track)" entries={langDetail.notes || []} empty="No notes yet across any country for this language." />
        <FeedCol title="Issues" icon={AlertTriangle} accent="var(--amber)" entries={langDetail.issues || []} empty="No issues raised across any country for this language." />
      </div>
    </section>
  );

  const load = async () => {
    const res = await api.get(`/language-projects/${projectId}`);
    setData(res.data);
    setDrafts(res.data.project.progress || {});
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/language-projects/${projectId}/progress`, { progress: drafts });
      toast.success("Progress updated");
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const openInfoEditor = () => {
    setInfoDraft({
      iso_code: data.project.iso_code || "",
      lang_status: data.project.lang_status || "",
      start_date: data.project.start_date || "",
      end_date: data.project.end_date || "",
      details: data.project.details || "",
    });
    setInfoOpen(true);
  };

  const saveInfo = async () => {
    setInfoSaving(true);
    try {
      // Backend ignores fields with None; send empty strings as null to clear
      const payload = { ...infoDraft };
      await api.patch(`/language-projects/${projectId}/info`, payload);
      toast.success("Language info updated");
      setInfoOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    } finally {
      setInfoSaving(false);
    }
  };

  if (!data) return <div className="p-10 mono text-xs text-[color:var(--ink-2)]">Loading…</div>;

  const { project, region, country, cluster } = data;
  const books = project.books;
  // Build stage-derived progress map from the Book-by-Book Stage Timeline (source of truth).
  // Heatmap and Book-by-Book graph read from this — never from project.progress directly.
  const stageProgress = {};
  (stageBooks || []).forEach((sb) => {
    const name = String(sb?.book || "").trim();
    if (name) stageProgress[name] = Number(sb.pct || 0);
  });
  // Only fall back to project.progress if stage data hasn't loaded yet.
  const effectiveProgress = (stageBooks && stageBooks.length) ? stageProgress : (project.progress || {});
  const started = books.filter((b) => Number(effectiveProgress?.[b] || 0) > 0);
  const completed = books.filter((b) => Number(effectiveProgress?.[b] || 0) >= 1);
  const endLabel = project.end_date && new Date(project.end_date) > new Date() ? "Expected End" : "Ended";
  const discrepancies = computeDiscrepancies(books, project.progress, stageBooks);
  const hasDiscrepancy = discrepancies.length > 0;

  return (
    <div className="max-w-[1600px] mx-auto px-10 py-10 fade-in-up" data-testid="project-detail-page">
      <nav className="mono text-[11px] uppercase tracking-widest text-[color:var(--ink-2)] mb-4">
        <Link to="/regions" className="hover:underline">Regions</Link>
        <span className="mx-2">/</span>
        <Link to={`/regions/${region.id}`} className="hover:underline">{region.name}</Link>
        <span className="mx-2">/</span>
        <Link to={`/countries/${country.id}`} className="hover:underline">{country.name}</Link>
        <span className="mx-2">/</span>
        <span className="text-[color:var(--ink)]">{project.name}</span>
      </nav>

      <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
            {cluster?.name || "Cluster Project"}
          </div>
          <h1 className="serif text-6xl tracking-tight leading-none">{project.name}</h1>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <StatusBadge status={project.status} />
            {project.iso_code && (
              <span className="chip" data-testid="iso-chip">
                <Hash size={10} strokeWidth={2} /> ISO · {project.iso_code}
              </span>
            )}
            {project.lang_status && project.lang_status !== "Active" && (
              <span className="chip chip-amber">{project.lang_status}</span>
            )}
            <span className="mono text-[11px] text-[color:var(--ink-2)]">
              Scope: {project.translation_scope}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1">
            Overall Progress
          </div>
          <div className="serif text-6xl tracking-tight">{fmt(project.overall)}</div>
          <div className="w-56 mt-3"><ProgressBar value={project.overall} /></div>
        </div>
      </div>

      {/* Language Info card */}
      <div className="hairline bg-white p-6 mb-8" data-testid="language-info-card">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Info size={14} strokeWidth={1.75} className="text-[color:var(--ink-2)]" />
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
              Language Info
            </div>
          </div>
          {canEdit && (
            <button
              className="btn-ghost text-xs flex items-center gap-1"
              onClick={openInfoEditor}
              data-testid="edit-info"
            >
              <Pencil size={12} /> Edit info
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <InfoCell icon={Hash} label="ISO Code" value={project.iso_code} mono />
          <InfoCell icon={Tag} label="Language Status" value={project.lang_status || "Active"} />
          <InfoCell icon={Calendar} label="Started" value={project.start_date} />
          <InfoCell icon={Calendar} label={endLabel} value={project.end_date} />
          <InfoCell icon={Info} label="Notes" value={project.details} />
        </div>
      </div>

      {/* Heatmap card */}
      {/* Language-DB detail: engagements + notes + issues (moved above heatmap) */}
      {langDetailBlock}

      {/* Heatmap card — hidden when it disagrees with the Stage Timeline (source of truth) */}
      {hasDiscrepancy ? (
        <div
          className="hairline bg-white p-6 mb-8 flex items-start gap-3"
          style={{ borderColor: "var(--amber)" }}
          data-testid="heatmap-hidden-notice"
        >
          <AlertTriangle size={18} className="mt-0.5" style={{ color: "var(--amber)" }} strokeWidth={2} />
          <div>
            <div className="serif text-xl mb-1">Heatmap & Book-by-Book graph hidden</div>
            <div className="text-sm text-[color:var(--ink-2)]">
              This project's heatmap and Book-by-Book progress bar disagree with the KAIROS
              <span className="mono text-xs mx-1">Book-by-Book Stage Timeline</span>
              on <span className="mono">{discrepancies.length}</span>{" "}
              book{discrepancies.length === 1 ? "" : "s"}. Please rely on the
              <span className="italic mx-1">Book-by-Book Stage Timeline</span>
              below — it is the source of truth. Re-upload the KAIROS workbook to reconcile.
            </div>
          </div>
        </div>
      ) : (
      <div className="hairline bg-white p-8 mb-8">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1">
              New Testament · 27 Books
            </div>
            <div className="serif text-2xl tracking-tight flex items-center flex-wrap">
              Book Coverage Heatmap
              <DiscrepancyIcon items={discrepancies} />
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {editing ? (
                <>
                  <button
                    className="btn-ghost text-sm"
                    onClick={() => { setEditing(false); setDrafts(project.progress); }}
                    data-testid="cancel-edit"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary text-sm flex items-center gap-1"
                    onClick={save}
                    disabled={saving}
                    data-testid="save-progress"
                  >
                    <Save size={14} /> Save
                  </button>
                </>
              ) : (
                <button
                  className="btn-ghost text-sm flex items-center gap-1"
                  onClick={() => setEditing(true)}
                  data-testid="edit-progress"
                >
                  <Pencil size={14} /> Edit progress
                </button>
              )}
            </div>
          )}
        </div>

        {!editing ? (
          <>
            <div className="max-w-3xl">
              <BookHeatmap books={books} progress={effectiveProgress} size="md" />
            </div>
            <div className="mt-6 flex flex-wrap gap-4 mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
              {HM_COLORS.map((c, i) => (
                <div key={c} className="flex items-center gap-2">
                  <div className="w-3 h-3" style={{ background: c }} />
                  {HM_LABELS[i]}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
            {books.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <label className="w-32 text-sm truncate">{b}</label>
                <input
                  type="number" min="0" max="100" step="1"
                  className="input-flat w-20 mono text-sm py-1"
                  value={Math.round((drafts[b] ?? 0) * 100)}
                  onChange={(e) => {
                    const pct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                    setDrafts((d) => ({ ...d, [b]: pct / 100 }));
                  }}
                  data-testid={`input-${b}`}
                />
                <span className="mono text-xs text-[color:var(--ink-3)]">%</span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Per-book table — hidden when the Stage Timeline disagrees with it */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!hasDiscrepancy && (
        <div className="hairline bg-white p-6">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-4">
            Book-by-Book
          </div>
          <div className="grid grid-cols-1 gap-2">
            {books.map((b) => {
              const v = Number(effectiveProgress?.[b] || 0);
              return (
                <div key={b} className="flex items-center gap-4">
                  <span className="text-sm w-40 truncate">{b}</span>
                  <div className="flex-1"><ProgressBar value={v} /></div>
                  <span className="mono text-xs w-10 text-right">{fmt(v)}</span>
                </div>
              );
            })}
          </div>
        </div>
        )}

        <div className="space-y-6">
          <div className="hairline bg-white p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-4">
              Progress Summary
            </div>
            <div className="grid grid-cols-3 gap-4">
              <SumCell label="Started" v={started.length} />
              <SumCell label="Completed" v={completed.length} />
              <SumCell label="Total" v={books.length} />
            </div>
          </div>
          <div className="hairline bg-white p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-4">
              Team
            </div>
            {project.team?.length ? (
              <ul className="text-sm space-y-2">
                {project.team.map((m) => <li key={m.email}>{m.name} <span className="text-[color:var(--ink-3)]">· {m.role}</span></li>)}
              </ul>
            ) : (
              <div className="text-sm text-[color:var(--ink-2)]">
                No team members assigned yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-book stage timeline */}
      <BookStages projectId={projectId} />

      {/* (Language-DB detail moved above the heatmap) */}
      {false && langDetail && (
        <section className="mt-8" data-testid="lang-db-panel">
          <div className="mb-4">
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
              From Language Database · ISO {langDetail.iso}
            </div>
            <h2 className="serif text-3xl tracking-tight">Engagements &amp; Field Log</h2>
          </div>

          {langDetail.engagements?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {langDetail.engagements.map((e) => (
                <div key={e.key} className="hairline bg-white p-5" data-testid={`lang-engagement-${e.key}`}>
                  <div className="flex items-baseline gap-3 flex-wrap mb-3">
                    <span className="serif text-2xl tracking-tight">{e.country}</span>
                    <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)]">
                      {e.iso}{e.rolv ? ` · ${e.rolv}` : ""}
                    </span>
                    {e.hub && <span className="mono text-[10px] px-1.5 py-0.5 border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>{e.hub}</span>}
                    {e.cluster && <span className="mono text-[10px] px-1.5 py-0.5 border border-[color:var(--border)] text-[color:var(--ink-2)]">{e.cluster}</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Organizations</div><div className="mt-0.5">{(e.organizations || []).join(", ") || "—"}</div></div>
                    <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Involvement</div><div className="mt-0.5">{(e.engagement_functions || []).join(" · ") || "—"}</div></div>
                    <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Activities</div><div className="mt-0.5">{(e.activities || []).join(", ") || "—"}</div></div>
                    <div><div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">Status</div><div className="mt-0.5">{(e.statuses || []).join(", ") || "—"}</div></div>
                  </div>
                  {(e.current_scope?.length || e.completed_products?.length) ? (
                    <div className="border-t border-[color:var(--border)] mt-4 pt-3 text-xs space-y-2">
                      {e.current_scope?.length > 0 && (
                        <div>
                          <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)] mb-1">Current Scope</div>
                          <div className="flex flex-wrap gap-1">
                            {e.current_scope.map((s) => (
                              <span key={s} className="mono px-1 py-0.5 border rounded-sm" style={{ fontSize: "9px", borderColor: "var(--accent)", color: "var(--accent)" }}>{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {e.completed_products?.length > 0 && (
                        <div>
                          <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)] mb-1">Completed Products</div>
                          <div className="flex flex-wrap gap-1">
                            {e.completed_products.map((p, i) => (
                              <span key={i} className="chip inline-flex items-center gap-1" style={{ borderColor: "var(--on-track)", color: "var(--on-track)" }}>
                                <PackageCheck size={10} />
                                {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.product}</a> : p.product}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FeedCol title="Notes" icon={StickyNote} accent="var(--on-track)" entries={langDetail.notes || []} empty="No notes yet across any country for this language." />
            <FeedCol title="Issues" icon={AlertTriangle} accent="var(--amber)" entries={langDetail.issues || []} empty="No issues raised across any country for this language." />
          </div>
        </section>
      )}

      {/* Edit info dialog */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent data-testid="info-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Edit Language Info</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mono text-[10px] uppercase tracking-widest block mb-1">ISO Code</label>
              <input
                className="input-flat"
                value={infoDraft.iso_code || ""}
                onChange={(e) => setInfoDraft({ ...infoDraft, iso_code: e.target.value })}
                placeholder="e.g., EBG"
                data-testid="info-iso"
              />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest block mb-1">Language Status</label>
              <select
                className="input-flat"
                value={infoDraft.lang_status || ""}
                onChange={(e) => setInfoDraft({ ...infoDraft, lang_status: e.target.value })}
                data-testid="info-lang-status"
              >
                <option value="">— Select —</option>
                <option value="Active">Active</option>
                <option value="Starting Soon">Starting Soon</option>
                <option value="Pending">Pending</option>
                <option value="Suspended">Suspended</option>
                <option value="Dropped">Dropped</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Started</label>
                <input
                  type="date"
                  className="input-flat"
                  value={infoDraft.start_date || ""}
                  onChange={(e) => setInfoDraft({ ...infoDraft, start_date: e.target.value })}
                  data-testid="info-start-date"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Expected End</label>
                <input
                  type="date"
                  className="input-flat"
                  value={infoDraft.end_date || ""}
                  onChange={(e) => setInfoDraft({ ...infoDraft, end_date: e.target.value })}
                  data-testid="info-end-date"
                />
              </div>
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest block mb-1">Notes / Details</label>
              <textarea
                className="input-flat"
                rows={3}
                value={infoDraft.details || ""}
                onChange={(e) => setInfoDraft({ ...infoDraft, details: e.target.value })}
                placeholder="Tok Pisin, French, other notes…"
                data-testid="info-details"
              />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost text-sm" onClick={() => setInfoOpen(false)}>Cancel</button>
            <button
              className="btn-primary text-sm flex items-center gap-1"
              onClick={saveInfo}
              disabled={infoSaving}
              data-testid="save-info"
            >
              <Save size={14} /> {infoSaving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SumCell({ label, v }) {
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">{label}</div>
      <div className="serif text-3xl tracking-tight">{v}</div>
    </div>
  );
}

function InfoCell({ icon: Icon, label, value, mono }) {
  const empty = value == null || value === "";
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] flex items-center gap-1 mb-1">
        <Icon size={10} strokeWidth={2} /> {label}
      </div>
      <div className={`text-sm ${empty ? "text-[color:var(--ink-3)] italic" : mono ? "mono uppercase tracking-wider" : ""}`}>
        {empty ? "—" : value}
      </div>
    </div>
  );
}

function FeedCol({ title, icon: Icon, accent, entries, empty }) {
  return (
    <div className="hairline bg-white p-5" data-testid={`feed-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} style={{ color: accent }} strokeWidth={2} />
        <span className="serif text-xl">{title}</span>
        <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] ml-1">({entries.length})</span>
      </div>
      {entries.length === 0 ? (
        <div className="text-sm text-[color:var(--ink-2)] italic">{empty}</div>
      ) : (
        <div className="space-y-3">
          {entries.map((it, i) => (
            <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: accent }}>
              <div className="text-sm whitespace-pre-wrap">{it.text}</div>
              <div className="mono text-[10px] text-[color:var(--ink-3)] mt-1">
                {it.user} · {new Date(it.created_at).toLocaleString()}
                {it.country && <> · <MapPin size={9} className="inline" /> {it.country}{it.rolv ? ` · ${it.rolv}` : ""}</>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
